import { twMerge } from "tailwind-merge";

export type Args = {
  gridSize: number;
  containerHeight: number;
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  is2xl: boolean;
  isContentShare: boolean;
};

export function makeVideoTileClassName({
  gridSize = 1,
  containerHeight = 0,
  isXs,
  isSm,
  isMd,
  isLg,
  isXl,
  is2xl,
  isContentShare,
}: Args) {
  const safeHeight = Number.isFinite(containerHeight)
    ? Math.max(0, containerHeight)
    : 0;

  const safeGridSize = Number.isFinite(gridSize) && gridSize > 0 ? gridSize : 1;

  const between6and12 = safeGridSize > 6 && safeGridSize <= 12;
  const between12and16 = safeGridSize > 12 && safeGridSize <= 16;
  const between16and25 = safeGridSize > 16 && safeGridSize <= 25;
  const between25and36 = safeGridSize > 25 && safeGridSize <= 36;
  const between36and50 = safeGridSize > 36 && safeGridSize <= 50;

  return twMerge(
    "relative aspect-video p-2 bg-transparent",
    !isXs && !isContentShare && "w-full",

    // 1 participant
    safeGridSize === 1 && safeHeight >= 640 && "basis-full mx-auto",
    safeGridSize === 1 && safeHeight >= 640 && isXs && "basis-full",
    safeGridSize === 1 && safeHeight >= 640 && isSm && "basis-full",
    safeGridSize === 1 && safeHeight >= 640 && isMd && "basis-full",
    safeGridSize === 1 && safeHeight >= 640 && isLg && "basis-full",
    safeGridSize === 1 && safeHeight >= 640 && isXl && "basis-3/4",
    safeGridSize === 1 && safeHeight >= 640 && is2xl && "basis-3/4",
    safeGridSize === 1 && safeHeight < 640 && "basis-full",
    safeGridSize === 1 && safeHeight < 640 && isXs && "basis-full",
    safeGridSize === 1 && safeHeight < 640 && isSm && "basis-full",
    safeGridSize === 1 && safeHeight < 640 && isMd && "basis-full",
    safeGridSize === 1 && safeHeight < 640 && isLg && "basis-3/4",
    safeGridSize === 1 && safeHeight < 640 && isXl && "basis-2/3",
    safeGridSize === 1 && safeHeight < 640 && is2xl && "basis-2/3",
    safeGridSize === 1 && safeHeight < 500 && "basis-full",
    safeGridSize === 1 && safeHeight < 500 && isXs && "basis-full",
    safeGridSize === 1 && safeHeight < 500 && isSm && "basis-full",
    safeGridSize === 1 && safeHeight < 500 && isMd && "basis-3/4",
    safeGridSize === 1 && safeHeight < 500 && isLg && "basis-1/2",
    safeGridSize === 1 && safeHeight < 500 && isXl && "basis-1/3",
    safeGridSize === 1 && safeHeight < 500 && is2xl && "basis-1/3",

    // 2 participants
    safeGridSize === 2 && safeHeight >= 640 && "basis-full mx-auto",
    safeGridSize === 2 && safeHeight >= 640 && isXs && "basis-1/2",
    safeGridSize === 2 && safeHeight >= 640 && isSm && "basis-1/2",
    safeGridSize === 2 && safeHeight >= 640 && isMd && "basis-1/2",
    safeGridSize === 2 && safeHeight >= 640 && isLg && "basis-1/2",
    safeGridSize === 2 && safeHeight >= 640 && isXl && "basis-1/2",
    safeGridSize === 2 && safeHeight >= 640 && is2xl && "basis-1/2",
    safeGridSize === 2 && safeHeight < 640 && "basis-full",
    safeGridSize === 2 && safeHeight < 640 && isXs && "basis-1/2",
    safeGridSize === 2 && safeHeight < 640 && isSm && "basis-1/2",
    safeGridSize === 2 && safeHeight < 640 && isMd && "basis-1/2",
    safeGridSize === 2 && safeHeight < 640 && isLg && "basis-1/2",
    safeGridSize === 2 && safeHeight < 640 && isXl && "basis-1/2",
    safeGridSize === 2 && safeHeight < 640 && is2xl && "basis-1/2",
    safeGridSize === 2 && safeHeight < 500 && "basis-full",
    safeGridSize === 2 && safeHeight < 500 && isXs && "basis-1/2",
    safeGridSize === 2 && safeHeight < 500 && isSm && "basis-1/2",
    safeGridSize === 2 && safeHeight < 500 && isMd && "basis-1/2",
    safeGridSize === 2 && safeHeight < 500 && isLg && "basis-1/3",
    safeGridSize === 2 && safeHeight < 500 && isXl && "basis-1/3",
    safeGridSize === 2 && safeHeight < 500 && is2xl && "basis-1/2",

    // 3 participants
    safeGridSize === 3 && safeHeight >= 660 && "basis-full mx-auto",
    safeGridSize === 3 && safeHeight >= 660 && isXs && "basis-1/2",
    safeGridSize === 3 && safeHeight >= 660 && isSm && "basis-1/2",
    safeGridSize === 3 && safeHeight >= 660 && isMd && "basis-1/2",
    safeGridSize === 3 && safeHeight >= 660 && isLg && "basis-1/2",
    safeGridSize === 3 && safeHeight >= 660 && isXl && "basis-1/3",
    safeGridSize === 3 && safeHeight >= 660 && is2xl && "basis-1/3",
    safeGridSize === 3 && safeHeight < 660 && "basis-full",
    safeGridSize === 3 && safeHeight < 660 && isXs && "basis-1/2",
    safeGridSize === 3 && safeHeight < 660 && isSm && "basis-1/2",
    safeGridSize === 3 && safeHeight < 660 && isMd && "basis-1/3",
    safeGridSize === 3 && safeHeight < 660 && isLg && "basis-1/3",
    safeGridSize === 3 && safeHeight < 660 && isXl && "basis-1/3",
    safeGridSize === 3 && safeHeight < 660 && is2xl && "basis-1/3",
    safeGridSize === 3 && safeHeight < 500 && "basis-full",
    safeGridSize === 3 && safeHeight < 500 && isXs && "basis-1/2",
    safeGridSize === 3 && safeHeight < 500 && isSm && "basis-1/2",
    safeGridSize === 3 && safeHeight < 500 && isMd && "basis-1/3",
    safeGridSize === 3 && safeHeight < 500 && isLg && "basis-1/3",
    safeGridSize === 3 && safeHeight < 500 && isXl && "basis-1/3",
    safeGridSize === 3 && safeHeight < 500 && is2xl && "basis-1/2",

    // 4 participants
    safeGridSize === 4 && safeHeight >= 640 && "basis-full",
    safeGridSize === 4 && safeHeight >= 640 && isXs && "basis-1/2",
    safeGridSize === 4 && safeHeight >= 640 && isSm && "basis-1/2",
    safeGridSize === 4 && safeHeight >= 640 && isMd && "basis-1/2",
    safeGridSize === 4 && safeHeight >= 640 && isLg && "basis-1/2",
    safeGridSize === 4 && safeHeight >= 640 && isXl && "basis-1/3",
    safeGridSize === 4 && safeHeight >= 640 && is2xl && "basis-1/4",
    safeGridSize === 4 && safeHeight < 640 && "basis-full",
    safeGridSize === 4 && safeHeight < 640 && isXs && "basis-1/2",
    safeGridSize === 4 && safeHeight < 640 && isSm && "basis-1/2",
    safeGridSize === 4 && safeHeight < 640 && isMd && "basis-1/2",
    safeGridSize === 4 && safeHeight < 640 && isLg && "basis-1/3",
    safeGridSize === 4 && safeHeight < 640 && isXl && "basis-1/3",
    safeGridSize === 4 && safeHeight < 640 && is2xl && "basis-1/2",
    safeGridSize === 4 && safeHeight < 640 && "basis-full",
    safeGridSize === 4 && safeHeight < 640 && isXs && "basis-1/2",
    safeGridSize === 4 && safeHeight < 640 && isSm && "basis-1/2",
    safeGridSize === 4 && safeHeight < 640 && isMd && "basis-1/2",
    safeGridSize === 4 && safeHeight < 640 && isLg && "basis-1/3",
    safeGridSize === 4 && safeHeight < 640 && isXl && "basis-1/3",
    safeGridSize === 4 && safeHeight < 640 && is2xl && "basis-1/2",
    safeGridSize === 4 && safeHeight < 500 && "basis-full",
    safeGridSize === 4 && safeHeight < 500 && isXs && "basis-1/2",
    safeGridSize === 4 && safeHeight < 500 && isSm && "basis-1/2",
    safeGridSize === 4 && safeHeight < 500 && isMd && "basis-1/3",
    safeGridSize === 4 && safeHeight < 500 && isLg && "basis-1/4",
    safeGridSize === 4 && safeHeight < 500 && isXl && "basis-1/4",
    safeGridSize === 4 && safeHeight < 500 && is2xl && "basis-1/2",

    // 5 participants
    safeGridSize === 5 && safeHeight >= 660 && "basis-full",
    safeGridSize === 5 && safeHeight >= 660 && isXs && "basis-1/2",
    safeGridSize === 5 && safeHeight >= 660 && isSm && "basis-1/2",
    safeGridSize === 5 && safeHeight >= 660 && isMd && "basis-1/3",
    safeGridSize === 5 && safeHeight >= 660 && isLg && "basis-1/3",
    safeGridSize === 5 && safeHeight >= 660 && isXl && "basis-1/3",
    safeGridSize === 5 && safeHeight >= 660 && is2xl && "basis-1/3",
    safeGridSize === 5 && safeHeight < 660 && "basis-full",
    safeGridSize === 5 && safeHeight < 660 && isXs && "basis-1/2",
    safeGridSize === 5 && safeHeight < 660 && isSm && "basis-1/3",
    safeGridSize === 5 && safeHeight < 660 && isMd && "basis-1/3",
    safeGridSize === 5 && safeHeight < 660 && isLg && "basis-1/3",
    safeGridSize === 5 && safeHeight < 660 && isXl && "basis-1/3",
    safeGridSize === 5 && safeHeight < 660 && is2xl && "basis-1/3",
    safeGridSize === 5 && safeHeight < 500 && "basis-full",
    safeGridSize === 5 && safeHeight < 500 && isXs && "basis-1/2",
    safeGridSize === 5 && safeHeight < 500 && isSm && "basis-1/3",
    safeGridSize === 5 && safeHeight < 500 && isMd && "basis-1/3",
    safeGridSize === 5 && safeHeight < 500 && isLg && "basis-1/3",
    safeGridSize === 5 && safeHeight < 500 && isXl && "basis-1/3",
    safeGridSize === 5 && safeHeight < 500 && is2xl && "basis-1/3",

    // 6 participants
    safeGridSize === 6 && safeHeight >= 660 && "basis-full",
    safeGridSize === 6 && safeHeight >= 660 && isXs && "basis-1/2",
    safeGridSize === 6 && safeHeight >= 660 && isSm && "basis-1/2",
    safeGridSize === 6 && safeHeight >= 660 && isMd && "basis-1/3",
    safeGridSize === 6 && safeHeight >= 660 && isLg && "basis-1/3",
    safeGridSize === 6 && safeHeight >= 660 && isXl && "basis-1/3",
    safeGridSize === 6 && safeHeight >= 660 && is2xl && "basis-1/3",
    safeGridSize === 6 && safeHeight < 660 && "basis-full",
    safeGridSize === 6 && safeHeight < 660 && isXs && "basis-1/2",
    safeGridSize === 6 && safeHeight < 660 && isSm && "basis-1/2",
    safeGridSize === 6 && safeHeight < 660 && isMd && "basis-1/3",
    safeGridSize === 6 && safeHeight < 660 && isLg && "basis-1/3",
    safeGridSize === 6 && safeHeight < 660 && isXl && "basis-1/3",
    safeGridSize === 6 && safeHeight < 660 && is2xl && "basis-1/3",
    safeGridSize === 6 && safeHeight < 500 && "basis-full",
    safeGridSize === 6 && safeHeight < 500 && isXs && "basis-1/2",
    safeGridSize === 6 && safeHeight < 500 && isSm && "basis-1/3",
    safeGridSize === 6 && safeHeight < 500 && isMd && "basis-1/3",
    safeGridSize === 6 && safeHeight < 500 && isLg && "basis-1/3",
    safeGridSize === 6 && safeHeight < 500 && isXl && "basis-1/4",
    safeGridSize === 6 && safeHeight < 500 && is2xl && "basis-1/4",

    // 6 - 12 participants
    between6and12 && safeHeight >= 660 && "basis-full",
    between6and12 && safeHeight >= 660 && isXs && "basis-1/2",
    between6and12 && safeHeight >= 660 && isSm && "basis-1/3",
    between6and12 && safeHeight >= 660 && isMd && "basis-1/4",
    between6and12 && safeHeight >= 660 && isLg && "basis-1/4",
    between6and12 && safeHeight >= 660 && isXl && "basis-1/4",
    between6and12 && safeHeight >= 660 && is2xl && "basis-1/4",
    between6and12 && safeHeight < 660 && "basis-full",
    between6and12 && safeHeight < 660 && isXs && "basis-1/2",
    between6and12 && safeHeight < 660 && isSm && "basis-1/4",
    between6and12 && safeHeight < 660 && isMd && "basis-1/4",
    between6and12 && safeHeight < 660 && isLg && "basis-1/4",
    between6and12 && safeHeight < 660 && isXl && "basis-1/5",
    between6and12 && safeHeight < 660 && is2xl && "basis-1/5",
    between6and12 && safeHeight < 500 && "basis-full",
    between6and12 && safeHeight < 500 && isXs && "basis-1/2",
    between6and12 && safeHeight < 500 && isSm && "basis-1/4",
    between6and12 && safeHeight < 500 && isMd && "basis-1/5",
    between6and12 && safeHeight < 500 && isLg && "basis-1/6",
    between6and12 && safeHeight < 500 && isXl && "basis-1/6",
    between6and12 && safeHeight < 500 && is2xl && "basis-1/6",

    between12and16 && (is2xl ? "basis-1/5" : isSm ? "basis-1/4" : "basis-full"),

    between16and25 && (is2xl ? "basis-1/6" : isSm ? "basis-1/5" : "basis-full"),

    between25and36 && (is2xl ? "basis-1/7" : isSm ? "basis-1/6" : "basis-full"),

    between36and50 && (is2xl ? "basis-1/8" : isSm ? "basis-1/7" : "basis-full"),

    safeGridSize > 50 &&
      (is2xl ? "basis-1/9" : isSm ? "basis-1/8" : "basis-full"),

    isContentShare && "basis-auto pr-2 h-full",

    !isXs && "basis-1/4 max-w-md"
  );
}

export default makeVideoTileClassName;
